import React from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../Kendo/Button';
import { Tooltip } from '../Kendo/Tooltip';
import { alertsOperations } from '../../../ducks/alerts';
import { downloadPdfFromBuffer } from './utils/pdfUtils';

/**
 * Download a PDF file from a given fetch function buffer return. Dispatches an alert if the download fails.
 * @param {Object} params - Parameters for the PDF download button.
 * @param {function} params.fetchPdf - Function to fetch the PDF data, which should return an ArrayBuffer.
 * @param {string} params.filename - The name of the file to be downloaded.
 * @param {string} [params.tooltip] - Tooltip text to display on hover.
 * @param {string} [params.buttonTitle] - Title for the button, defaults to 'Download PDF'.
 * @param {string} [params.buttonContent] - Content for the button, defaults to 'Download PDF'.
 * @param {string} [params.themeColor] - Theme color for the button, defaults to 'info'.
 * @param {string} [params.toolTipPosition] - Position of the tooltip, defaults to 'top'.
 * @param {string} [params.toolTipAnchorElement] - The anchor element for the tooltip, defaults to 'target'.
 * @param {Object} [buttonProps] - Additional props to pass to the Button component.
 * @returns 
 */
export const PdfDownloadButton = ({
    fetchPdf,
    filename,
    tooltip,
    buttonTitle = 'Download PDF',
    buttonContent='Download PDF',
    themeColor = 'info',
    toolTipPosition = 'top',
    toolTipAnchorElement = 'target',
    ...buttonProps
}) => {
    const dispatch = useDispatch();

    const handleDownload = async () => {
        try {
            const bufferArray = await fetchPdf();
            downloadPdfFromBuffer(bufferArray, filename);
        } catch (error) {
            console.error('Error downloading the PDF:', error);
            dispatch(alertsOperations.addAlert('Error downloading the PDF. Please try again.', 'danger'));
        }
    };

    return (
        <Tooltip content={tooltip} position={toolTipPosition} anchorElement={toolTipAnchorElement}>
            <Button
                onClick={handleDownload}
                themeColor={themeColor}
                content={buttonContent}
                title={buttonTitle} // This is required for Kendo Tooltip to target
                {...buttonProps}
            />
        </Tooltip>
    );
};